import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowUpRight, KeyRound, Sparkles, BarChart3, LogOut, Store } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { BRAND } from "@/data/content";
import { listProjects } from "@/lib/vault";

function StatCard({ label, value, hint, testid }) {
  return (
    <div
      data-testid={testid}
      className="border border-neutral-200 bg-white p-6 sm:p-8"
    >
      <p className="overline">{label}</p>
      <p className="font-serif text-3xl sm:text-4xl text-gold mt-3 sm:mt-4 leading-none">
        {value}
      </p>
      {hint && <p className="text-xs text-neutral-500 mt-3">{hint}</p>}
    </div>
  );
}

function getPlanHint(plan, isPending) {
  if (isPending) return "Pick a plan to unlock the Studio";
  if (plan === "signature") return "Enterprise · unlimited";
  return "Monthly billing · cancel anytime";
}

const QUICK_STARTS = [
  {
    icon: Sparkles,
    title: "Build with the Studio",
    body: "Describe your idea in plain English. Pick a lead form, content pack, brand kit or recommender - and publish it in one click.",
    cta: "Open the Studio Builder",
    to: "/dashboard/builder",
    testid: "qs-build",
  },
  {
    icon: KeyRound,
    title: "Open your Vault",
    body: "Every draft you save lives here. Open one to preview, edit, publish or share.",
    cta: "Open the Vault",
    to: "/dashboard/vault",
    testid: "qs-vault",
  },
  {
    icon: Store,
    title: "Monetize your audience",
    body: "Spin up a link-in-bio storefront with downloads, courses, memberships and bookings. Pre-filled by industry.",
    cta: "Open the Monetization Hub",
    to: "/dashboard/monetize",
    testid: "qs-monetize",
  },
  {
    icon: BarChart3,
    title: "See before / after results",
    body: "Open Success Insights to track what changed - leads, posts published, conversions - week over week.",
    cta: "Open Success Insights",
    to: "/dashboard/insights",
    testid: "qs-insights",
    comingSoon: true,
  },
];

export default function DashboardHome() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [projectCount, setProjectCount] = useState(null);

  useEffect(() => {
    let cancelled = false;
    listProjects()
      .then((data) => {
        if (!cancelled) setProjectCount(Array.isArray(data) ? data.length : 0);
      })
      .catch(() => {
        if (!cancelled) setProjectCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const firstName = (user?.name || user?.email || "there").split(" ")[0].split("@")[0];
  const plan = (user?.plan || "pending").toLowerCase();
  const isPending = plan === "pending" && user?.role !== "admin";
  const planLabel = plan === "pending" ? "NO PLAN" : plan.toUpperCase();
  const planHint = getPlanHint(plan, isPending);

  return (
    <div data-testid="dashboard-home" className="min-h-screen bg-[#F7F5EF] text-neutral-900">
      <header className="border-b border-neutral-200/70">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 h-16 sm:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-2 sm:gap-3" data-testid="dash-logo">
            <span className="font-serif text-lg sm:text-2xl tracking-tight text-neutral-900">
              {BRAND.wordmark.lead}
            </span>
            <span className="font-serif italic text-lg sm:text-2xl text-gold">
              {BRAND.wordmark.tail}
            </span>
          </Link>
          <div className="flex items-center gap-3 sm:gap-5">
            <span className="hidden sm:inline text-xs uppercase tracking-[0.18em] text-neutral-500" data-testid="dash-user-email">
              {user?.email}
            </span>
            <button
              onClick={onLogout}
              data-testid="dash-logout"
              className="inline-flex items-center gap-2 border border-neutral-200 hover:border-gold hover:text-gold px-3 py-2 text-xs uppercase tracking-[0.18em] transition"
            >
              <LogOut size={12} /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 py-12 sm:py-16">
        <section data-testid="dash-hero">
          <p className="overline">GoldKey Vault · Welcome</p>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.05] mt-4 sm:mt-6">
            Hi {firstName} -{" "}
            <span className="italic text-aurora">let&apos;s ship something.</span>
          </h1>
          <p className="text-neutral-500 leading-relaxed mt-5 sm:mt-6 max-w-2xl">
            Three quick starts below. You can build with the Studio, activate
            the Growth Engine, or open Success Insights to see what changed
            last week. The full onboarding tour and your project Vault land
            in the next update.
          </p>
        </section>

        <section className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mt-10 sm:mt-12">
          <StatCard
            label="Plan"
            value={planLabel}
            hint={planHint}
            testid="stat-plan"
          />
          <StatCard
            label="Credits remaining"
            value={(user?.credits_remaining ?? 0).toLocaleString()}
            hint="Used by Studio Builder runs, posts and Advisor sessions"
            testid="stat-credits"
          />
          <StatCard
            label="Projects"
            value={projectCount ?? "..."}
            hint={projectCount && projectCount > 0 ? "Open the Vault to manage them" : "Start one with the Builder"}
            testid="stat-projects"
          />
        </section>

        {isPending && (
          <section
            data-testid="dash-paywall"
            className="mt-10 sm:mt-12 relative overflow-hidden aurora-border rounded-md p-6 sm:p-10 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center"
          >
            <div aria-hidden className="aurora-orb aurora-orb-magenta orb-anim-a" style={{ width: 280, height: 280, top: -120, right: -60, opacity: 0.16 }} />
            <div className="relative lg:col-span-8">
              <p className="overline">Activate the Studio</p>
              <h2 className="font-serif text-2xl sm:text-3xl mt-3 leading-snug">
                Your account is created. Pick a plan to{" "}
                <span className="italic text-aurora">unlock everything.</span>
              </h2>
              <p className="text-neutral-700 mt-4 max-w-xl leading-relaxed">
                Starter at <span className="text-aurora-static font-medium">$49/mo</span>, Growth at{" "}
                <span className="text-aurora-static font-medium">$199/mo</span>. Monthly billing via Stripe,
                annual saves 20%, cancel any time. You&apos;ll be inside the Studio in under
                a minute.
              </p>
              {user?.brand_voice?.business_name && (
                <p
                  data-testid="dash-brand-voice"
                  className="text-xs uppercase tracking-[0.2em] text-aurora-static mt-5 inline-flex items-center gap-2 border border-gold/30 px-3 py-1.5 rounded-sm"
                >
                  ✓ Studio calibrated for {user.brand_voice.business_name}
                </p>
              )}
            </div>
            <div className="relative lg:col-span-4 flex flex-col gap-3">
              <Link
                to="/pricing"
                data-testid="dash-paywall-pricing"
                className="btn-aurora justify-center"
              >
                See plans
              </Link>
              <a
                href="https://calendly.com/morneaultr/30min"
                target="_blank"
                rel="noreferrer"
                data-testid="dash-paywall-calendly"
                className="btn-ghost justify-center"
              >
                Book a call
              </a>
            </div>
          </section>
        )}

        <section className="mt-14 sm:mt-20">
          <div className="flex items-end justify-between flex-wrap gap-5 mb-8 sm:mb-10">
            <div>
              <p className="overline">Pick a quick start</p>
              <h2 className="font-serif text-3xl sm:text-4xl tracking-tight leading-[1.05] mt-3">
                Where to <span className="italic text-aurora">begin.</span>
              </h2>
            </div>
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-neutral-500">
              <KeyRound size={12} className="text-gold" /> All plans · all of these
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
            {QUICK_STARTS.map((q) => (
              <div
                key={q.title}
                data-testid={q.testid}
                className="group border border-neutral-200 bg-white p-6 sm:p-8 hover:border-gold/40 transition flex flex-col"
              >
                <q.icon size={20} className="text-gold" strokeWidth={1.4} />
                <h3 className="font-serif text-xl sm:text-2xl mt-5 sm:mt-6">{q.title}</h3>
                <p className="text-sm text-neutral-500 mt-3 leading-relaxed flex-1">{q.body}</p>
                {q.comingSoon ? (
                  <button
                    type="button"
                    disabled
                    className="mt-6 flex flex-col items-start gap-1.5 text-xs uppercase tracking-[0.18em] text-neutral-400 cursor-not-allowed text-left"
                    data-testid={`${q.testid}-cta`}
                    aria-disabled="true"
                    title="Available in the next update"
                  >
                    <span className="inline-flex items-center gap-2">{q.cta} <ArrowUpRight size={12} /></span>
                    <span className="text-[10px] tracking-[0.2em] text-gold/70">Coming next</span>
                  </button>
                ) : (
                  <Link
                    to={q.to}
                    data-testid={`${q.testid}-cta`}
                    className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-gold hover:text-gold transition"
                  >
                    {q.cta} <ArrowUpRight size={12} />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14 sm:mt-20 border border-gold/20 bg-gold/[0.03] p-6 sm:p-8 lg:p-10">
          <p className="overline">What&apos;s next</p>
          <h2 className="font-serif text-2xl sm:text-3xl mt-3 leading-snug">
            Growth Engine and the weekly recap email
            <br />
            land in <span className="italic text-aurora">Phase 4.</span>
          </h2>
          <p className="text-neutral-500 mt-4 max-w-2xl leading-relaxed">
            Phase 3 just shipped the Vault and Studio Builder. Phase 4 wires
            the Growth Engine (draft + schedule + export). Phase 5 adds the
            Stripe webhook so paid plans activate automatically the moment a
            customer&apos;s payment clears.
          </p>
        </section>
      </main>
    </div>
  );
}
